import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchGetData } from "../dataVar/apiService";
import appVar from "../dataVar/appVar";
import logger from "./logger";

import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Collapse,
    Divider,
    Box,
    Typography,
    styled,
    IconButton,
    Avatar,
    Tooltip,
    Button,
} from "@mui/material";

import {
    Person,
    ExitToApp,
    Public,
    AccountCircle,
    Home
} from '@mui/icons-material';



//https://mui.com/components/material-icons/
import * as MuiIcons from "@mui/icons-material";
import {
    Menu,
    ChevronLeft,
    ExpandLess,
    ExpandMore,
} from "@mui/icons-material";

// Evita que el navegador sirva una versión vieja cacheada del logo al reemplazar el archivo en el servidor.
const CACHE_BUST = Date.now();

// Estilos personalizados
const SidebarContainer = styled(Drawer)(({ theme, collapsed }) => ({
    "& .MuiDrawer-paper": {
        width: collapsed ? 60 : 270, // Aumentado de 50 a 60 para mejor UX
        backgroundColor: appVar.gColorCorp,
        color: appVar.gColorLetraMenu,
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: "hidden",
        position: "fixed", // Asegurar posición fija
        height: "100vh", // Altura completa
        zIndex: theme.zIndex.drawer, // Z-index apropiado
        "& .MuiListItemIcon-root": {
            color: appVar.gColorLetraMenu,
            minWidth: collapsed ? "auto" : "40px",
            marginRight: collapsed ? 0 : theme.spacing(1),
            justifyContent: "center", // Centrar iconos cuando está colapsado
        },
        "& .MuiListItemText-root": {
            opacity: collapsed ? 0 : 1,
            transition: "opacity 0.2s",
        },
    },
}));

const MenuComponent = () => {
    const [menuData, setMenuData] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(() => {
        // Inicializar desde localStorage si existe
        const saved = localStorage.getItem('menuCollapsed');
        return saved ? JSON.parse(saved) : false;
    });
    const [openSections, setOpenSections] = useState({});
    const [nomRegional, setNomRegional] = useState(appVar.gNomRegional);

    // Mantener el nombre de la regional sincronizado cuando cambia desde otro módulo (CambiaRegional)
    useEffect(() => {
        const handleRegionalChange = (e) => setNomRegional(e.detail);
        window.addEventListener("appvar:gNomRegional", handleRegionalChange);
        return () => window.removeEventListener("appvar:gNomRegional", handleRegionalChange);
    }, []);

    // Función para alternar el estado colapsado
    const toggleCollapsed = () => {
        const newCollapsed = !collapsed;
        setCollapsed(newCollapsed);
        localStorage.setItem('menuCollapsed', JSON.stringify(newCollapsed));
    };



    const handleToggle = (sectionId) => {
        setOpenSections((prev) => {
            const newState = { ...prev };

            // Buscar el nodo seleccionado y su nivel
            const findNode = (items, id) => {
                for (const item of items) {
                    if (item.ID === id) {
                        return item;
                    }
                    if (item.children && item.children.length > 0) {
                        const found = findNode(item.children, id);
                        if (found) return found;
                    }
                }
                return null;
            };

            const selectedNode = findNode(menuData, sectionId);

            if (selectedNode && selectedNode.Nivel === 1) {
                // Si es padre, cerrar todos los padres menos el seleccionado
                menuData.forEach(item => {
                    if (item.ID !== sectionId) {
                        newState[item.ID] = false;
                    }
                });
            }

            // Alternar el nodo clicado
            newState[sectionId] = !prev[sectionId];

            return newState;
        });
    };



    useEffect(() => {
        const fetchMenuData = async () => {
            try {


                const username = appVar.gCodUsuario;
                const data = await fetchGetData(
                    "api/Login-Acceso/MenuUser",
                    `${username}`
                );

                // Transformar los datos planos a estructura jerárquica
                const hierarchicalData = buildMenuTree(data);
                setMenuData(hierarchicalData);

                // Inicializar secciones abiertas
                const initialOpenState = {};
                hierarchicalData.forEach(item => {
                    if (item.children && item.children.length > 0) {
                        initialOpenState[item.ID] = false;
                    }
                });
                setOpenSections(initialOpenState);
            } catch (error) {
                logger.error("Error fetching menu data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchMenuData();
    }, []);

    // Función para construir la estructura jerárquica del menú
    const buildMenuTree = (flatData) => {
        const menuMap = {};
        const rootItems = [];

        // Primero crear todos los items en el mapa
        flatData.forEach(item => {
            menuMap[item.ID] = {
                ...item,
                children: []
            };
        });

        // Luego asignar los hijos a sus padres
        flatData.forEach(item => {
            if (item.ParentID && item.ParentID !== item.ID && menuMap[item.ParentID]) {
                menuMap[item.ParentID].children.push(menuMap[item.ID]);
            } else if (!item.ParentID || item.ParentID === item.ID) {
                rootItems.push(menuMap[item.ID]);
            }
        });

        // Ordenar los items por su propiedad Orden
        const sortMenuItems = (items) => {
            return items.sort((a, b) => a.Orden - b.Orden).map(item => {
                if (item.children.length > 0) {
                    item.children = sortMenuItems(item.children);
                }
                return item;
            });
        };

        return sortMenuItems(rootItems);
    };

    // Componente recursivo para renderizar el menú con los nuevos estilos
    const renderMenuItem = (item) => {
        const hasChildren = item.children && item.children.length > 0;
        const IconComponent = item.Icon ? MuiIcons[item.Icon] : null;

        return (
            <React.Fragment key={item.ID}>
                <ListItem
                    button
                    onClick={() => {
                        if (hasChildren) {
                            handleToggle(item.ID);
                        } else if (item.NavigateURL) {
                            navigate(item.NavigateURL);
                        }
                    }}
                    sx={{
                        justifyContent: collapsed ? "center" : "flex-start",
                        pl: item.Nivel > 1 ? (item.Nivel) * 2 : 2,
                        py: 0.5 // Reduce el padding vertical
                    }}
                >
                    {IconComponent && (
                        <ListItemIcon sx={{ minWidth: '40px' }}>
                            <IconComponent />
                        </ListItemIcon>
                    )}
                    {!collapsed && (
                        <>
                            <ListItemText
                                primary={item.NombreMenu}
                                slotProps={{
                                    primary: {
                                        fontSize: `${1 - (item.Nivel * 0.1)}rem`, // Reduce tamaño por nivel
                                        fontWeight: item.Nivel > 1 ? 'normal' : 'medium'
                                    }
                                }}
                            />
                            {hasChildren && (openSections[item.ID] ? <ExpandLess /> : <ExpandMore />)}
                        </>
                    )}
                </ListItem>
                {hasChildren && !collapsed && (
                    <Collapse in={openSections[item.ID]} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {item.children.map(child => renderMenuItem(child))}
                        </List>
                    </Collapse>
                )}
            </React.Fragment>
        );
    };

    const handleLogout = () => {
        navigate('/login');
    };
    const handleMiUser = () => {
        navigate('/CambiaPassword');
    };
    const handleCambRegional = () => {
        navigate('/CambiaRegional');
    };
    const handleAcerca = () => {
        navigate('/Acerca');
    };
    const handleCondiciones = () => {
        navigate('/Terminos');
    };

    const LogoCliente = `/imagenes/LogoCliente.png?v=${CACHE_BUST}`;
    const FondoUser = `/imagenes/FondoUser.png?v=${CACHE_BUST}`;

    const regionalEnabled = String(appVar.gOpcionReg) === "1";

    return (
        <SidebarContainer variant="permanent" collapsed={collapsed}>
            {/* Header con botón de hamburguesa */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "space-between",
                    p: 2,
                    height: 64,
                }}
            >
                {!collapsed && (
                    <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", mr: 1 }}>
                        <img
                            src={LogoCliente}
                            alt="Logo"
                            style={{
                                maxHeight: 60,
                                maxWidth: '100%',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                marginTop: 30,
                                marginBottom: 20
                            }}
                        />
                    </Box>
                )}

                <IconButton
                    onClick={toggleCollapsed}
                    sx={{ color: appVar.gColorLetraMenu }}
                >
                    {collapsed ? <Menu /> : <ChevronLeft />}
                </IconButton>
            </Box>

            {/* Menú dinámico */}
            <Divider sx={{ bgcolor: "rgba(190, 23, 23, 0.1)" }} />
            <List>
                {loading ? (
                    <Typography variant="body2" sx={{ p: 2, color: appVar.gColorLetraMenu }}>
                        Cargando menú...
                    </Typography>
                ) : (
                    menuData.map(item => renderMenuItem(item))
                )}
            </List>

            {/* Footer */}
            <Divider sx={{ bgcolor: "rgba(190, 23, 23, 0.1)" }} />

            <Box
                sx={{
                    mt: 'auto',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    color: appVar.gColorLetraMenu,
                    backgroundImage: `url(${FondoUser})`,
                    backgroundSize: '100% 100%',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                {/* Sección de usuario */}



                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        mb: 1,
                    }}
                >
                    <Tooltip title="Usuario" placement="right">
                        <Avatar sx={{ bgcolor: '#7467ef', width: 28, height: 28 }}>
                            <Person />
                        </Avatar>
                    </Tooltip>
                    {!collapsed && (
                        <Box sx={{ ml: 2 }}>
                            <Typography variant="body1" sx={{ color: appVar.gColorLetraMenu, fontSize: '12px', display: 'block' }}>
                                {appVar.gNomUsuario}
                            </Typography>
                            <Typography variant="caption" sx={{ color: appVar.gColorLetraMenu, fontSize: '12px', display: 'block' }}>
                                Regional: {nomRegional}
                            </Typography>
                            <Typography variant="caption" sx={{ color: appVar.gColorLetraMenu, fontSize: '12px', display: 'block' }}>
                                Fecha de Proceso: {appVar.gFecProceso ? new Date(appVar.gFecProceso).toLocaleDateString('es-ES', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit'
                                }) : ''}
                            </Typography>
                        </Box>
                    )}
                </Box>


                {!collapsed && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, p: 0.1 }}>

                        <Tooltip title="Inicio" placement="top">
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<Home />}
                                onClick={() => navigate('/Inicio')}
                                sx={{
                                    color: appVar.gColorLetraMenu,
                                    borderColor: appVar.gColorLetraMenu,
                                    minWidth: 58,
                                    px: 0.5,
                                    '&:hover': { borderColor: 'rgba(255,255,255,0.8)' },
                                    flex: '0 1 32px',
                                    mb: 0,
                                }}
                            />
                        </Tooltip>

                        <Tooltip title="Mi Usuario" placement="top">
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AccountCircle />}
                                onClick={handleMiUser}
                                sx={{
                                    color: appVar.gColorLetraMenu,
                                    borderColor: appVar.gColorLetraMenu,
                                    minWidth: 58,
                                    px: 0.5,
                                    '&:hover': { borderColor: 'rgba(255,255,255,0.8)' },
                                    flex: '0 1 32px'
                                }}
                            />
                        </Tooltip>

                        <Tooltip title="Cambiar Regional" placement="top">
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<Public />}
                                onClick={handleCambRegional}
                                disabled={!regionalEnabled}
                                sx={{
                                    color: appVar.gColorLetraMenu,
                                    borderColor: appVar.gColorLetraMenu,
                                    minWidth: 58,
                                    px: 0.5,
                                    '&:hover': { borderColor: 'rgba(255,255,255,0.8)' },
                                    '&:disabled': {
                                        color: 'rgba(255,255,255,0.3)',
                                        borderColor: 'rgba(255,255,255,0.3)'
                                    },
                                    flex: '0 1 32px'
                                }}
                            />
                        </Tooltip>



                        <Tooltip title="Salir" placement="top">
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<ExitToApp />}
                                onClick={handleLogout}
                                sx={{
                                    color: appVar.gColorLetraMenu,
                                    borderColor: appVar.gColorLetraMenu,
                                    minWidth: 58,
                                    px: 0.5,
                                    '&:hover': { borderColor: 'rgba(255,255,255,0.8)' },
                                    flex: '0 1 32px',
                                    mb: 0,
                                }}
                            />
                        </Tooltip>
                    </Box>
                )}

            </Box>

            {!collapsed && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        px: 2,
                        py: 1,
                    }}
                >
                    {/* Columna 1: Logo (Izquierda) */}
                    <Box>
                        <img
                            src="/imagenes/LogoXUECia.png"
                            alt="Logo"
                            style={{ height: 40, marginTop: 10, marginBottom: 5 }}
                        />
                    </Box>

                    {/* Columna 2: Botones (Derecha) */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end', // Alineados a la derecha
                            gap: 0.1,
                        }}
                    >
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleCondiciones}
                            sx={{
                                color: appVar.gColorLetraMenu,
                                fontSize: '10px',
                                textTransform: 'none',
                                minWidth: 'auto',
                                p: 0.1,
                                '&:hover': {
                                    color: appVar.gColorLetraMenu,
                                    backgroundColor: 'transparent',
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            Términos y Condiciones
                        </Button>

                        <Button
                            variant="text"
                            size="small"
                            onClick={handleAcerca}
                            sx={{
                                color: appVar.gColorLetraMenu,
                                fontSize: '10px',
                                textTransform: 'none',
                                minWidth: 'auto',
                                p: 0.1,
                                '&:hover': {
                                    color: appVar.gColorLetraMenu,
                                    backgroundColor: 'transparent',
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            Acerca de
                        </Button>
                    </Box>
                </Box>
            )}


        </SidebarContainer>
    );
};

// Exportar tanto el componente como una función para obtener el ancho del menú
export const getMenuWidth = (collapsed = false) => {
    return collapsed ? 60 : 270;
};

// Hook personalizado para usar en otros componentes
export const useMenuWidth = () => {
    const [collapsed, setCollapsed] = useState(false);

    // En una implementación real, esto debería sincronizarse con el estado del menú
    // Por ahora, asumimos que se puede obtener del localStorage o context
    useEffect(() => {
        const savedCollapsed = localStorage.getItem('menuCollapsed') === 'true';
        setCollapsed(savedCollapsed);
    }, []);

    return getMenuWidth(collapsed);
};

export default MenuComponent;